import styled, { css, keyframes } from "styled-components";
import { Button } from "../../components/ButtonV2/Button";
import { Title } from "../../components/Title/Title";
import { BAR_LAYOUT_REM } from "./constants";
import { OverlapMode, SectionPosition } from "./types";
import {
  BarVariant,
  ButtonVariant,
  ContactKind,
  MegaMenuAlign,
  MegaMenuAspect,
  MegaMenuCardAlign,
  MegaMenuCardLayout,
  MegaMenuDensity,
  MegaMenuWidth,
  MenuAlign,
  MenuSpacing,
  MobileDividers,
  MobileDrawerSide,
  MobileMenuMode,
  SubmenuStyle,
  UnitsMobileAlign,
  UnitsSelectorStyle,
  type ContactBadgeRadius,
  type PaddingProps,
} from "./types";

const MOBILE = "@media (max-width: 767px)";
const DESKTOP = "@media (min-width: 768px)";
const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

const whenMobile = (rules: ReturnType<typeof css> | string) => css`
  ${MOBILE} {
    ${rules}
  }
  [data-viewport="mobile"] & {
    ${rules}
  }
`;

const whenDesktop = (rules: ReturnType<typeof css> | string) => css`
  ${DESKTOP} {
    ${rules}
  }
  [data-viewport="desktop"] & {
    ${rules}
  }
`;

const Z_ROOT = 9999;

const gutters = css`
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  @media (min-width: 576px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

const hiddenScrollbar = css`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const panelIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const EASE_OUT = "cubic-bezier(0.22, 0.61, 0.36, 1)";

const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0.6; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-100%); opacity: 0.6; }
  to { transform: translateX(0); opacity: 1; }
`;

const drawerFullscreenIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 1; }
  100% { opacity: 0.45; }
`;

const positionCss: Record<SectionPosition, ReturnType<typeof css>> = {
  [SectionPosition.RELATIVE]: css`
    position: relative;
  `,
  [SectionPosition.STICKY]: css`
    position: sticky !important;
    top: 0;
  `,
  [SectionPosition.ABSOLUTE]: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  `,
};

export const Root = styled.section<{ $position: SectionPosition; $overlap: OverlapMode }>`
  width: 100%;
  z-index: ${Z_ROOT};
  ${({ $position }) => positionCss[$position]}

  ${({ $overlap }) =>
    $overlap === OverlapMode.COLLAPSE &&
    css`
      height: 0;
      overflow: visible;
    `}
`;

const resolvePadding = ({
  unit = "px",
  values,
}: {
  unit?: string;
  values?: { paddingTop?: number; paddingBottom?: number };
}) => {
  return {
    top: `${values?.paddingTop ?? 0}${unit}`,
    bottom: `${values?.paddingBottom ?? 0}${unit}`,
  };
};

export const Surface = styled.div<{
  $hidden: boolean;
  $blur: number;
  $padding?: PaddingProps;
}>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: var(--hv2-background);
  border-bottom: 1px solid var(--hv2-border);
  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    transform 0.25s ease;

  ${({ $blur }) =>
    $blur > 0 &&
    css`
      -webkit-backdrop-filter: blur(${$blur}px);
      backdrop-filter: blur(${$blur}px);
    `}

  ${({ $hidden }) =>
    $hidden &&
    css`
      transform: translateY(-100%);
    `}

  ${({ $padding }) => {
    const unit = $padding?.typeMeasure || undefined;
    const desktop = resolvePadding({ unit, values: $padding?.desktop });
    const mobile = resolvePadding({ unit, values: $padding?.mobile });
    return css`
      ${whenDesktop(`padding-top: ${desktop.top}; padding-bottom: ${desktop.bottom};`)}
      ${whenMobile(`padding-top: ${mobile.top}; padding-bottom: ${mobile.bottom};`)}
    `;
  }}

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Bar = styled.div<{ $variant: BarVariant; $topBar?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  ${gutters}

  ${({ $topBar }) =>
    $topBar &&
    css`
      background-color: var(--hv2-topbar-bg);
      border-bottom: 1px solid var(--hv2-topbar-border);
    `}

  ${({ $variant }) =>
    $variant === BarVariant.TOP
      ? css`
          min-height: 4.5rem;
          ${whenMobile("min-height: 4rem;")}
        `
      : css`
          min-height: 3.25rem;
          ${whenMobile("display: none;")}
        `}
`;

export const BarInner = styled.div<{ $compact: boolean }>`
  width: 100%;
  max-width: ${({ $compact }) => ($compact ? `${BAR_LAYOUT_REM.compactMaxWidth}rem` : "none")};
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${BAR_LAYOUT_REM.barGap}rem;
  ${whenMobile("gap: 0.75rem;")}
`;

export const LogosWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`;

export const LogoDivider = styled.div`
  width: 1px;
  height: 1.75rem;
  background-color: var(--hv2-divider);
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  ${whenMobile("flex-shrink: 1; min-width: 0;")}

  a {
    display: flex;
    min-width: 0;
    transition: opacity 0.2s ease;

    &:hover,
    &:focus-visible {
      opacity: 0.8;
    }

    ${REDUCED_MOTION} {
      transition: none;
    }
  }

  .image,
  img {
    max-height: 2rem;
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: left center;
    ${whenMobile("max-height: 1.75rem;")}
  }
`;

export const Nav = styled.nav<{ $align?: MenuAlign; $withLogo?: boolean }>`
  display: none;
  flex-shrink: 1;
  align-self: stretch;
  align-items: stretch;
  margin-right: auto;
  margin-left: ${({ $align, $withLogo }) =>
    $align === MenuAlign.CENTER ? "auto" : $withLogo ? `${BAR_LAYOUT_REM.menuLogoMargin}rem` : "0"};
  ${whenDesktop("display: flex;")}
  ${whenMobile("display: none;")}
`;

export const MenuItems = styled.ul<{ $spacing?: MenuSpacing }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: var(--hv2-menu-wrap, wrap);
  align-items: stretch;
  column-gap: ${({ $spacing }) => ($spacing === MenuSpacing.SPACIOUS ? "3.25rem" : "2rem")};
  row-gap: 0;
`;

export const MenuItem = styled.li`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const menuTextCss = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.2;
  white-space: var(--hv2-menu-text-wrap, normal);
  text-align: left;
  color: var(--hv2-links);
  background: none;
  border: 0;
  cursor: pointer;
  text-decoration: none;
  transition:
    color 0.2s ease,
    opacity 0.2s ease;

  .hv2-menu-icon {
    display: inline-flex;
    flex-shrink: 0;
    --icon-color: currentColor;

    svg {
      width: 1rem;
      height: 1rem;
    }
  }

  &:hover,
  &:focus-visible {
    opacity: 0.7;
  }

  &[aria-expanded="true"] {
    color: var(--hv2-links-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-links-hover);
    outline-offset: -2px;
    border-radius: 2px;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const MenuItemLink = styled.a`
  ${menuTextCss}
`;

export const MenuItemButton = styled.button`
  ${menuTextCss}

  .hv2-chevron {
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] .hv2-chevron {
    transform: rotate(180deg);
  }
`;

export const Submenu = styled.div<{ $wide?: boolean; $columns?: number; $style?: SubmenuStyle }>`
  position: absolute;
  top: ${({ $style }) =>
    $style === SubmenuStyle.FLOATING
      ? "calc(100% + 2px + var(--hv2-submenu-drop, 0px))"
      : "calc(100% + var(--hv2-submenu-drop, 0px))"};
  left: var(--hv2-submenu-shift, 0px);
  min-width: ${({ $wide, $columns }) => ($wide ? `${14 + 8 * ($columns ?? 1)}rem` : "14rem")};
  max-width: min(52rem, 92vw);
  z-index: 20;
  padding: 0.5rem 0;
  background-color: var(--hv2-submenu);
  border: 1px solid var(--hv2-submenu-border);

  ${({ $style }) =>
    $style === SubmenuStyle.FLOATING
      ? css`
          border-radius: var(--hv2-submenu-radius, 8px);
          box-shadow: 0 18px 32px -18px rgba(0, 0, 0, 0.28);
        `
      : css`
          border-top: 0;
          border-radius: 0 0 var(--hv2-submenu-radius, 8px) var(--hv2-submenu-radius, 8px);
          box-shadow: 0 16px 24px -16px rgba(0, 0, 0, 0.18);
        `}
  animation: ${panelIn} 0.2s ease;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const SubmenuLinks = styled.ul<{ $columns?: number }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns ?? 1}, minmax(0, 1fr));
  gap: ${({ $columns }) => (($columns ?? 1) > 1 ? "0.25rem 1.5rem" : "0")};
`;

export const SubmenuLink = styled.a<{ $withDescription?: boolean }>`
  display: flex;
  align-items: ${({ $withDescription }) => ($withDescription ? "flex-start" : "center")};
  gap: 0.625rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: ${({ $withDescription }) => ($withDescription ? "normal" : "nowrap")};
  color: var(--hv2-submenu-text);
  text-decoration: none;
  transition: opacity 0.15s ease;

  .hv2-submenu-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--hv2-submenu-icon);
    --icon-color: var(--hv2-submenu-icon);
    margin-top: ${({ $withDescription }) => ($withDescription ? "0.125rem" : "0")};

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  .hv2-submenu-body {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .hv2-submenu-desc {
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.4;
    color: var(--hv2-submenu-desc);
  }

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-submenu-text);
    outline-offset: -2px;
  }
`;

export const MegaMenuPanel = styled.div<{ $fullscreen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  background-color: var(--hv2-mm-panel-bg);
  border-top: 1px solid var(--hv2-mm-panel-border);
  box-shadow: 0 24px 32px -24px rgba(0, 0, 0, 0.18);
  max-height: calc(var(--hv2-viewport, 100vh) * 0.7);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--hv2-mm-title) 25%, transparent) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 9999px;
    background-color: color-mix(in srgb, var(--hv2-mm-title) 25%, transparent);
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: color-mix(in srgb, var(--hv2-mm-title) 40%, transparent);
  }

  animation: ${panelIn} 0.2s ease;

  @media (min-height: 1200px) {
    max-height: calc(var(--hv2-viewport, 100vh) * 0.82);
  }

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      min-height: calc(var(--hv2-viewport, 100vh) - 100%);
      max-height: calc(var(--hv2-viewport, 100vh) - 100%);
    `}

  ${whenMobile("display: none;")}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const MegaMenuInner = styled.div<{ $width: MegaMenuWidth; $align: MegaMenuAlign; $density: MegaMenuDensity }>`
  width: 100%;

  ${({ $density }) =>
    $density === MegaMenuDensity.COMPACT
      ? css`
          padding-top: 1rem;
          padding-bottom: 1.5rem;
          ${gutters}
        `
      : css`
          padding: 1.5rem clamp(1.5rem, 5%, 6rem) 2rem;
        `}

  ${({ $width, $align }) =>
    $width === MegaMenuWidth.CONTENT
      ? css`
          max-width: 76rem;
          margin: ${$align === MegaMenuAlign.START ? "0 auto 0 0" : $align === MegaMenuAlign.END ? "0 0 0 auto" : "0 auto"};
        `
      : css`
          max-width: none;
        `}
`;

export const MegaMenuGrid = styled.ul<{ $columns: number; $density?: MegaMenuDensity; $layout?: MegaMenuCardLayout }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: ${({ $density, $layout }) =>
    $layout === MegaMenuCardLayout.BESIDE
      ? $density === MegaMenuDensity.COMPACT
        ? "0.75rem 1.25rem"
        : "1.25rem 2rem"
      : $density === MegaMenuDensity.COMPACT
        ? "1.5rem 1.25rem"
        : "2.5rem 2rem"};
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));

  ${whenMobile("gap: 1.25rem;")}
`;

export const MegaMenuFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

export const MegaMenuSeeAll = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--hv2-mm-seeall);
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.7;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg,
  &:focus-visible svg {
    transform: translateX(4px);
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-mm-title);
    outline-offset: 4px;
    border-radius: 2px;
  }
`;

export const MegaMenuCard = styled.a<{ $layout: MegaMenuCardLayout }>`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  max-width: 22rem;

  ${whenMobile("max-width: none;")}
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.7;
  }

  ${({ $layout }) =>
    $layout === MegaMenuCardLayout.BESIDE &&
    css`
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    `}

  .hv2-arrow {
    transition: transform 0.2s ease;
  }

  &:hover .hv2-arrow,
  &:focus-visible .hv2-arrow {
    transform: translateX(4px);
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-mm-title);
    outline-offset: 4px;
    border-radius: 4px;
  }

  ${REDUCED_MOTION} {
    transition: none;

    .hv2-arrow {
      transition: none;
    }
  }
`;

export const MegaMenuThumb = styled.div<{ $aspect: MegaMenuAspect; $layout: MegaMenuCardLayout }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: var(--design-card-border-radius, 8px);
  background-color: var(--hv2-mm-thumb-bg);

  ${({ $aspect }) =>
    $aspect === MegaMenuAspect.FIXED &&
    css`
      aspect-ratio: 16 / 10;
    `}

  ${({ $layout }) =>
    $layout === MegaMenuCardLayout.BESIDE &&
    css`
      width: clamp(7rem, 40%, 12rem);
      flex-shrink: 0;
    `}

  img {
    display: block;
    width: 100%;
    height: ${({ $aspect }) => ($aspect === MegaMenuAspect.FIXED ? "100%" : "auto")};
    object-fit: contain;
  }
`;

export const MegaMenuCardBody = styled.div<{ $align?: MegaMenuCardAlign }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;

  align-items: ${({ $align }) => ($align === MegaMenuCardAlign.CENTER ? "center" : "flex-start")};
  text-align: ${({ $align }) => ($align === MegaMenuCardAlign.CENTER ? "center" : "left")};
`;

export const MegaMenuTag = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--hv2-mm-tag);
`;

export const MegaMenuTitle = styled(Title)`
  color: var(--hv2-mm-title);

  ${whenMobile("font-size: 1.125rem; line-height: 1.3;")}
`;

export const MegaMenuDescription = styled.span`
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--hv2-mm-description);

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const MegaMenuCardCta = styled.span<{ $variant?: ButtonVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  .hv2-arrow {
    display: inline-flex;
    --icon-color: currentColor;

    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;

  ${({ $variant }) =>
    $variant === ButtonVariant.FILLED
      ? css`
          padding: 0.5rem 1rem;
          color: var(--hv2-mm-cta, var(--hv2-buttons-text));
          background-color: var(--hv2-mm-cta-bg, var(--hv2-buttons-background));
          border: 1px solid var(--hv2-mm-cta-bg, var(--hv2-buttons-border));
          border-radius: var(--design-button-border-radius, 6px);
        `
      : $variant === ButtonVariant.OUTLINE
        ? css`
            padding: 0.5rem 1rem;
            color: var(--hv2-mm-cta, var(--hv2-outline-text));
            background-color: var(--hv2-mm-cta-bg, transparent);
            border: 1px solid var(--hv2-outline-border);
            border-radius: var(--design-button-border-radius, 6px);
          `
        : css`
            padding: 0;
            color: var(--hv2-mm-cta, var(--hv2-link-text));
          `}
`;

export const MegaMenuSkeleton = styled.li<{ $aspect: MegaMenuAspect; $layout: MegaMenuCardLayout }>`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  animation: ${shimmer} 1.2s ease-in-out infinite;

  ${({ $layout }) =>
    $layout === MegaMenuCardLayout.BESIDE &&
    css`
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const SkeletonThumb = styled.div<{ $aspect: MegaMenuAspect; $layout: MegaMenuCardLayout }>`
  width: 100%;
  aspect-ratio: ${({ $aspect }) => ($aspect === MegaMenuAspect.FIXED ? "16 / 10" : "16 / 9")};
  border-radius: var(--design-card-border-radius, 8px);
  background-color: var(--hv2-mm-panel-border);

  ${({ $layout }) =>
    $layout === MegaMenuCardLayout.BESIDE &&
    css`
      width: clamp(7rem, 40%, 12rem);
      flex-shrink: 0;
    `}
`;

export const SkeletonLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

export const SkeletonLine = styled.div<{ $width: string }>`
  height: 0.75rem;
  width: ${({ $width }) => $width};
  border-radius: 9999px;
  background-color: var(--hv2-mm-panel-border);
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

export const DesktopOnly = styled.div`
  display: none;
  align-items: center;
  gap: ${BAR_LAYOUT_REM.desktopGroupGap}rem;
  min-width: 0;
  ${whenDesktop("display: flex;")}
  ${whenMobile("display: none;")}
`;

export const MobileOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${whenDesktop("display: none;")}
  ${whenMobile("display: flex;")}
`;

const resolveContactRadius = ({ radius }: { radius: ContactBadgeRadius }) => (radius === "pill" ? "9999px" : radius);

export const ActionIconSlot = styled.div<{ $kind: ContactKind; $radius: ContactBadgeRadius }>`
  display: flex;

  > div > button {
    height: ${BAR_LAYOUT_REM.contactSize}rem;
    width: ${BAR_LAYOUT_REM.contactSize}rem;
    padding: 0;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
    color: ${({ $kind }) => ($kind === ContactKind.WHATSAPP ? "var(--hv2-wa-icon)" : "var(--hv2-phone-icon)")};
    background-color: ${({ $kind }) => ($kind === ContactKind.WHATSAPP ? "var(--hv2-wa-bg)" : "var(--hv2-phone-bg)")};
    border: 1px solid ${({ $kind }) => ($kind === ContactKind.WHATSAPP ? "var(--hv2-wa-border)" : "var(--hv2-phone-border)")};
    border-radius: ${({ $radius }) => resolveContactRadius({ radius: $radius })};
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      color 0.2s ease,
      opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }

    .label {
      line-height: 1;
    }

    .hv2-contact-icon {
      display: inline-flex;
      --icon-color: currentColor;
    }

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }
`;

export const DesktopButtonLink = styled.a<{ $minRem: number }>`
  display: flex;
  min-width: ${({ $minRem }) => $minRem}rem;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }

  > * {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

const buttonVariantCss = ({ variant, accent }: { variant?: ButtonVariant; accent?: string }) => {
  switch (variant) {
    case ButtonVariant.OUTLINE:
      return css`
        background-color: transparent;
        border-color: ${accent || "var(--hv2-outline-border)"};
        color: ${accent || "var(--hv2-outline-text)"};
        --icon-color: ${accent || "var(--hv2-outline-text)"};
      `;
    case ButtonVariant.LINK:
      return css`
        background-color: transparent;
        border-color: transparent;
        color: ${accent || "var(--hv2-link-text)"};
        --icon-color: ${accent || "var(--hv2-link-text)"};
        text-decoration: underline;
        text-underline-offset: 0.2em;
        padding-left: 0;
        padding-right: 0;
      `;
    default:
      return css``;
  }
};

export const DesktopButtonText = styled(Button)<{ $variant?: ButtonVariant; $accent?: string }>`
  && {
    height: 2.5rem;
    padding: 0 1.25rem;
    gap: ${BAR_LAYOUT_REM.buttonIconGap}rem;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    border-radius: var(--design-button-border-radius, 6px);
    ${({ $variant, $accent }) => buttonVariantCss({ variant: $variant, accent: $accent })}
  }

  span {
    display: flex;
    flex-shrink: 0;
  }

  .hv2-button-text {
    display: block;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    width: ${BAR_LAYOUT_REM.buttonIcon}rem;
    height: ${BAR_LAYOUT_REM.buttonIcon}rem;
    flex-shrink: 0;
  }
`;

export const MobileCta = styled.a<{ $bg?: string; $color?: string; $border?: string; $variant?: ButtonVariant; $accent?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.25rem;
  max-width: 9rem;
  min-width: 5rem;
  flex-shrink: 1;
  padding: 0 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  border-radius: var(--design-button-border-radius, 6px);
  border: 1px solid ${({ $border }) => $border || "var(--hv2-buttons-border)"};
  background-color: ${({ $bg }) => $bg || "var(--hv2-buttons-background)"};
  color: ${({ $color }) => $color || "var(--hv2-buttons-text)"};
  --icon-color: ${({ $color }) => $color || "var(--hv2-buttons-text)"};
  ${({ $variant, $accent }) => buttonVariantCss({ variant: $variant, accent: $accent })}

  > span {
    display: flex;
    min-width: 0;
  }

  .hv2-cta-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
  }

  &:hover {
    opacity: 0.85;
  }
`;

export const Hamburger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-right: -0.625rem;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--hv2-icons);
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid var(--hv2-links-hover);
    outline-offset: 2px;
    border-radius: 9999px;
  }

  ${whenDesktop("display: none;")}
  ${whenMobile("display: flex;")}
`;

export const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${BAR_LAYOUT_REM.rightGroupGap}rem;
  margin-left: auto;
  flex-shrink: 1;
  min-width: 0;
  ${whenDesktop("flex-shrink: 1000000; min-width: var(--hv2-right-min, 0px);")}
  ${whenMobile("gap: 0.75rem;")}

  > * {
    min-width: 0;
  }

  > [data-header-buttons] {
    ${whenDesktop("min-width: var(--hv2-buttons-min, 0px);")}
  }

  > [data-header-units] {
    ${whenDesktop(`min-width: ${BAR_LAYOUT_REM.truncatedMinWidth}rem;`)}
  }
`;

export const GroupDivider = styled.span`
  width: 1px;
  height: 1.5rem;
  background-color: var(--hv2-units-divider);
  flex-shrink: 0;
  ${whenMobile("display: none;")}
`;

export const UnitsSlot = styled.div<{ $style: UnitsSelectorStyle; $iconRight?: boolean; $mobileAlign: UnitsMobileAlign }>`
  display: flex;
  min-width: 0;

  > div > button {
    height: 2.25rem;
    padding: 0 0.875rem;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--hv2-units-text);
    --icon-color: var(--hv2-units-text);
    border-radius: var(--design-button-border-radius, 6px);
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      color 0.2s ease;

    ${({ $style }) =>
      $style === UnitsSelectorStyle.TEXT
        ? css`
            padding: 0;
          `
        : css`
            border: 1px solid var(--hv2-units-border);
            background-color: var(--hv2-units-bg);
            border-radius: ${$style === UnitsSelectorStyle.ROUND ? "9999px" : "0.5rem"};
          `}

    > span:first-child {
      display: flex;
    }

    ${({ $iconRight }) =>
      $iconRight &&
      css`
        > *:first-child {
          order: 1;
        }
        > *:last-child {
          order: 2;
        }
      `}

    svg {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      color: var(--hv2-units-text);
    }
  }

  > div > button > span:last-of-type {
    max-width: 14rem;
  }

  > div > div[role="listbox"] {
    ${whenDesktop("left: auto; right: 0;")}
    ${({ $mobileAlign }) =>
      whenMobile(
        $mobileAlign === UnitsMobileAlign.START
          ? "left: 0; right: auto;"
          : $mobileAlign === UnitsMobileAlign.END
            ? "left: auto; right: 0;"
            : "left: 50%; right: auto; transform: translateX(-50%);",
      )}
    background-color: var(--hv2-drawer-bg);
    border: 1px solid var(--hv2-divider);
    border-radius: var(--design-card-border-radius, 8px);
    box-shadow: 0 16px 24px -16px rgba(0, 0, 0, 0.18);
    scrollbar-width: thin;
    scrollbar-color: color-mix(in srgb, var(--hv2-drawer-text) 25%, transparent) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: color-mix(in srgb, var(--hv2-drawer-text) 25%, transparent);
      border-radius: 9999px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }

    li {
      color: var(--hv2-drawer-text);
      background-color: transparent;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: color-mix(in srgb, var(--hv2-drawer-text) 8%, transparent);
      }
    }

    li[aria-selected="true"],
    li[data-selected="true"] {
      color: var(--hv2-links-hover);
      background-color: color-mix(in srgb, var(--hv2-links-hover) 10%, transparent);
    }
  }
`;

export const UnitBarMobile = styled.div<{ $align: UnitsMobileAlign; $divider: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === UnitsMobileAlign.START ? "flex-start" : $align === UnitsMobileAlign.END ? "flex-end" : "center")};
  min-height: 3rem;
  padding: 0.5rem 1.5rem;
  background-color: var(--hv2-topbar-bg);
  border-bottom: 1px solid ${({ $divider }) => ($divider ? "var(--hv2-topbar-border)" : "transparent")};
  ${whenDesktop("display: none;")}
  ${whenMobile("display: flex;")}
`;

export const DrawerTopSlot = styled.div`
  margin: 0 -1rem;
  padding: 0.25rem 1rem 0.75rem;
  border-bottom: 1px solid var(--hv2-divider);
`;

export const DrawerUnitsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 0;
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  color: var(--hv2-drawer-text);
  background: none;
  border: 0;
  cursor: pointer;

  .hv2-units-value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .hv2-units-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hv2-units-icon {
    display: inline-flex;
    flex-shrink: 0;
    --icon-color: currentColor;

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  .hv2-chevron-right {
    flex-shrink: 0;
    transform: rotate(-90deg);
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: -2px;
  }
`;

export const DrawerUnitItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 0;
  font-size: 0.9375rem;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  line-height: 1.3;
  text-align: left;
  color: var(--hv2-drawer-text);
  background: none;
  border: 0;
  cursor: pointer;
  transition: opacity 0.15s ease;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: -2px;
  }
`;

export const DrawerBackdrop = styled.button<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${Z_ROOT};
  border: 0;
  padding: 0;
  cursor: default;
  background-color: ${({ $visible }) => ($visible ? "rgba(15, 23, 42, 0.4)" : "transparent")};
  animation: ${fadeIn} 0.28s ${EASE_OUT};
  ${whenDesktop("display: none;")}
  ${whenMobile("display: block;")}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Drawer = styled.div<{ $mode: MobileMenuMode; $side?: MobileDrawerSide }>`
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: ${Z_ROOT + 1};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  ${hiddenScrollbar}
  background-color: var(--hv2-drawer-bg);
  color: var(--hv2-drawer-text);
  padding: 0 1rem 2rem;

  ${({ $mode, $side = MobileDrawerSide.RIGHT }) =>
    $mode === MobileMenuMode.FULLSCREEN
      ? css`
          left: 0;
          right: 0;
          animation: ${drawerFullscreenIn} 0.26s ${EASE_OUT};
        `
      : $side === MobileDrawerSide.LEFT
        ? css`
            left: 0;
            width: min(85vw, 22rem);
            box-shadow: 8px 0 24px rgba(0, 0, 0, 0.12);
            animation: ${slideInLeft} 0.32s ${EASE_OUT};
          `
        : css`
            right: 0;
            width: min(85vw, 22rem);
            box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
            animation: ${slideInRight} 0.32s ${EASE_OUT};
          `}

  ${whenDesktop("display: none;")}
  ${whenMobile("display: flex;")}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const DrawerHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 4rem;
  margin: 0 -1rem 0.5rem;
  padding: 0 1rem;
  background-color: inherit;
  border-bottom: 1px solid var(--hv2-divider);

  > *:first-child {
    display: flex;
    align-items: center;
    min-width: 0;
  }
`;

export const DrawerClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  margin-right: -0.625rem;
  margin-left: auto;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--hv2-drawer-text);

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: 2px;
    border-radius: 9999px;
  }
`;

export const DrawerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const DrawerItem = styled.li<{ $dividers?: MobileDividers }>`
  ${({ $dividers = MobileDividers.LINES }) =>
    $dividers === MobileDividers.LINES &&
    css`
      border-bottom: 1px solid var(--hv2-divider);
    `}
`;

const drawerTextCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.875rem 0;
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  color: var(--hv2-drawer-text);
  background: none;
  border: 0;
  cursor: pointer;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: -2px;
  }
`;

export const DrawerItemLink = styled.a`
  ${drawerTextCss}
`;

export const DrawerItemButton = styled.button`
  ${drawerTextCss}

  .hv2-chevron-right {
    flex-shrink: 0;
    transform: rotate(-90deg);
    opacity: 0.6;
  }
`;

export const DrawerSubList = styled.ul<{ $dividers?: MobileDividers }>`
  list-style: none;
  margin: 0;
  padding: 0 0 1rem;

  > li + li {
    ${({ $dividers = MobileDividers.LINES }) =>
      $dividers === MobileDividers.LINES
        ? css`
            border-top: 1px solid var(--hv2-divider);
          `
        : css``}
  }
`;

export const DrawerSubLink = styled.a<{ $withDescription?: boolean }>`
  display: flex;
  align-items: ${({ $withDescription }) => ($withDescription ? "flex-start" : "center")};
  gap: 0.625rem;
  padding: 0.875rem 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--hv2-drawer-text);
  text-decoration: none;

  .hv2-submenu-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--hv2-drawer-text);
    --icon-color: var(--hv2-drawer-text);
    margin-top: ${({ $withDescription }) => ($withDescription ? "0.125rem" : "0")};

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  .hv2-submenu-body {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .hv2-submenu-desc {
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.4;
    color: color-mix(in srgb, var(--hv2-drawer-text) 80%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: -2px;
  }
`;

export const DrawerPanel = styled.div`
  padding: 0 0 1.25rem;

  --hv2-mm-title: var(--hv2-drawer-text);
  --hv2-mm-description: color-mix(in srgb, var(--hv2-drawer-text) 80%, transparent);
  --hv2-mm-tag: color-mix(in srgb, var(--hv2-drawer-text) 80%, transparent);
  --hv2-mm-seeall: var(--hv2-drawer-text);
  --hv2-mm-thumb-bg: color-mix(in srgb, var(--hv2-drawer-text) 8%, transparent);
`;

export const DrawerSubPanel = styled.div<{ $side?: MobileDrawerSide }>`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  ${hiddenScrollbar}
  background-color: var(--hv2-drawer-bg);
  color: var(--hv2-drawer-text);
  padding: 0 1rem 2rem;
  animation: ${({ $side = MobileDrawerSide.RIGHT }) => ($side === MobileDrawerSide.LEFT ? slideInLeft : slideInRight)} 0.28s ${EASE_OUT};

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const DrawerSubHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 4rem;
  margin: 0 -1rem 0.5rem;
  padding: 0 1rem;
  background-color: inherit;
  border-bottom: 1px solid var(--hv2-divider);
`;

export const DrawerBack = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-left: -0.625rem;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--hv2-drawer-text);

  &:focus-visible {
    outline: 2px solid var(--hv2-drawer-text);
    outline-offset: 2px;
    border-radius: 9999px;
  }
`;

export const DrawerSubTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--hv2-drawer-text);
`;

export const DrawerFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1.5rem;
`;

export const DrawerButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  a,
  a > * {
    width: 100%;
  }
`;

export const DrawerContacts = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
